import { StringListFilterComponent } from "../../../filters/StringListFilter";
import { GridTableFilterRow, HeaderData } from "../../../ui-components/GridTable/GridTableFilterRow";

export type MultiselectCriteria<TCriteriaKey extends string, TItemType> = { [key in TCriteriaKey]: TItemType[] };
export type MultiselectFilterChangeFunction<TCriteriaKey extends string, TItemType> = (
  key: TCriteriaKey,
  value: TItemType[],
) => void;

export const multiselectFilter =
  <TCriteriaKey extends string, TItemKey extends string, TItemType>(
    criteriaKey: TCriteriaKey,
    itemKey: TItemKey,
    equalFn?: (a: TItemType, b: TItemType) => boolean,
  ) =>
  (item: { [key in TItemKey]: TItemType }, constrains: MultiselectCriteria<TCriteriaKey, TItemType>) => {
    if (constrains[criteriaKey].length === 0) {
      return true;
    }

    if (equalFn === undefined) {
      return constrains[criteriaKey].includes(item[itemKey]);
    }

    return constrains[criteriaKey].find((a) => equalFn(a, item[itemKey])) !== undefined;
  };

export const MultiselectFilterComponent = <TCriteriaKey extends string, TItemType>({
  criteriaKey,
  criteria,
  onFilterChange,
  items,
  itemToString,
  stringToItem,
}: {
  criteriaKey: TCriteriaKey;
  criteria: MultiselectCriteria<TCriteriaKey, TItemType>;
  onFilterChange: MultiselectFilterChangeFunction<TCriteriaKey, TItemType>;
  items: TItemType[];
  itemToString: (a: TItemType) => string;
  stringToItem: (a: string) => TItemType;
}) => (
  <StringListFilterComponent
    small
    selected={criteria[criteriaKey].map(itemToString)}
    onChange={(value) => onFilterChange(criteriaKey, value.map(stringToItem))}
    items={items.map(itemToString)}
  />
);

export const MultiselectColumnDefinition = <
  TCriteriaKey extends string,
  TItemKey extends string,
  TItemType,
  TRow extends {
    [key in TItemKey]: TItemType;
  },
>(
  name: React.ReactNode,
  itemKey: TItemKey,
  criteriaKey: TCriteriaKey,
  criteria: MultiselectCriteria<TCriteriaKey, TItemType>,
  onFilterChange: MultiselectFilterChangeFunction<TCriteriaKey, TItemType>,
  items: TItemType[],
  itemToString: (a: TItemType) => string,
  stringToItem: (a: string) => TItemType,
  width?: number,
  itemRenderer?: (row: TRow) => React.ReactNode,
) => ({
  key: itemKey,
  name: <>{name}</>,
  headerCellClass: "GridTableFilterRow__header-cell",
  formatter: ({ row }: { row: TRow }) => (itemRenderer !== undefined ? itemRenderer(row) : <>{row[itemKey]}</>),
  headerRenderer: (header: HeaderData) => (
    <GridTableFilterRow header={header}>
      <MultiselectFilterComponent
        criteriaKey={criteriaKey}
        criteria={criteria}
        onFilterChange={onFilterChange}
        items={items}
        itemToString={itemToString}
        stringToItem={stringToItem}
      />
    </GridTableFilterRow>
  ),
  width: width ?? 150,
  sortable: true,
});
